import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { SlipPayment, adminApi, BASE_URL, ExpiredOrderNoSlip } from '../services/api';
import {
  slipPaymentStatusColor,
  slipPaymentStatusLabel,
  orderStatusColor,
  orderStatusLabel,
} from '../lib/statusLabels';

interface Props { onSelect: (p: SlipPayment) => void; }

type SlipTimePreset = 'all' | 'since_1' | 'since_3' | 'since_7' | 'custom';

const PAGE_SIZE = 25;
const emptySlipTimeQuery: Record<string, never> = {};

const SlipListPage: React.FC<Props> = ({ onSelect }) => {
  const [pays, setPays] = useState<SlipPayment[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('submitted');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [error, setError] = useState('');
  const [expiredNoSlip, setExpiredNoSlip] = useState<ExpiredOrderNoSlip[]>([]);
  const [timePreset, setTimePreset] = useState<SlipTimePreset>('since_1');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [slipSort, setSlipSort] = useState<'asc' | 'desc'>('desc');
  const [showExpiredNoOrders, setShowExpiredNoOrders] = useState(false);

  const slipTimeQuery = useMemo(() => {
    if (timePreset === 'all') return emptySlipTimeQuery;
    if (timePreset === 'since_1') return { since_days: 1 };
    if (timePreset === 'since_3') return { since_days: 3 };
    if (timePreset === 'since_7') return { since_days: 7 };
    const o: { created_from?: string; created_to?: string } = {};
    if (customFrom.trim()) o.created_from = customFrom.trim();
    if (customTo.trim()) o.created_to = customTo.trim();
    if (!o.created_from && !o.created_to) return emptySlipTimeQuery;
    return o;
  }, [timePreset, customFrom, customTo]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchInput.trim()), 380);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, filter, timePreset, customFrom, customTo, slipSort, showExpiredNoOrders]);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const base = {
        page,
        limit: PAGE_SIZE,
        q: debouncedSearch || undefined,
        sort: slipSort,
        include_expired_no_slip: showExpiredNoOrders,
        ...slipTimeQuery,
      };
      const res =
        filter === 'submitted'
          ? await adminApi.getPending(base)
          : await adminApi.getAll({
              ...base,
              status: filter === 'all' ? undefined : filter,
            });
      setPays(res.payments ?? []);
      setTotal(Number(res.total) || 0);
      setHasMore(Boolean(res.has_more));
      setExpiredNoSlip(showExpiredNoOrders ? (res.expired_orders_no_slip ?? []) : []);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [filter, page, debouncedSearch, slipTimeQuery, slipSort, showExpiredNoOrders]);

  useEffect(() => {
    load();
  }, [load]);

  const fmt = (iso: string) => {
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return 'ไม่ระบุเวลา';
      return d.toLocaleString('th-TH', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch {
      return 'ไม่ระบุเวลา';
    }
  };

  const badge = (label: string, raw: string, colors: Record<string, string>) => {
    const c = colors[raw] ?? '#6b7280';
    return (
      <span style={{ borderRadius: 20, padding: '4px 10px', fontSize: 11, fontWeight: 600, background: c + '18', color: c, whiteSpace: 'nowrap' }}>
        {label}
      </span>
    );
  };

  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = total === 0 ? 0 : (page - 1) * PAGE_SIZE + pays.length;

  const showExpiredNoSlip = showExpiredNoOrders && expiredNoSlip.length > 0;
  const showSlipCards = pays.length > 0;
  const entirelyEmpty = !loading && !showExpiredNoSlip && !showSlipCards;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <p style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.15em', color: '#888', textTransform: 'uppercase', marginBottom: 2 }}>จัดการสลิป</p>
          <h2 style={{ fontSize: 22, fontWeight: 700 }}>รายการสลิป</h2>
        </div>
        <button type="button" onClick={() => load()} style={{ padding: '8px 16px', borderRadius: 20, border: '1.5px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
          🔄 รีเฟรช
        </button>
      </div>

      {/* Filter tabs — สถานะสลิป */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        {[['submitted', '🕐 รอตรวจ'], ['all', '📋 ทั้งหมด'], ['verified', '✅ อนุมัติ'], ['rejected', '❌ ปฏิเสธ'], ['expired', '⌛ หมดเวลา']].map(([v, label]) => (
          <button
            key={v}
            type="button"
            onClick={() => { setFilter(v); setTimePreset('all'); setCustomFrom(''); setCustomTo(''); setSlipSort(v === 'submitted' ? 'asc' : 'desc'); }}
            style={{ padding: '6px 16px', borderRadius: 20, border: '1.5px solid', cursor: 'pointer', fontSize: 13, fontWeight: filter === v ? 700 : 500, background: filter === v ? '#1a0f0c' : '#fff', color: filter === v ? '#fff' : '#444', borderColor: filter === v ? '#1a0f0c' : '#e5e7eb' }}>
            {label}
          </button>
        ))}
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, cursor: 'pointer', fontSize: 13, color: '#525252' }}>
        <input
          type="checkbox"
          checked={showExpiredNoOrders}
          onChange={(e) => setShowExpiredNoOrders(e.target.checked)}
          style={{ width: 17, height: 17 }}
        />
        <span>แสดงออเดอร์ที่<strong>หมดเวลาชำระ</strong>แต่ยัง<strong>ไม่อัปโหลดสลิป</strong></span>
      </label>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: '#888', marginRight: 4 }}>เรียงเวลาอัปโหลดสลิป:</span>
        <button
          type="button"
          onClick={() => setSlipSort('asc')}
          style={{
            padding: '4px 12px',
            borderRadius: 16,
            border: '1px solid',
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: slipSort === 'asc' ? 700 : 500,
            background: slipSort === 'asc' ? '#eff6ff' : '#fff',
            color: '#444',
            borderColor: slipSort === 'asc' ? '#3b82f6' : '#e5e7eb',
          }}
        >
          เก่าก่อน (ASC)
        </button>
        <button
          type="button"
          onClick={() => setSlipSort('desc')}
          style={{
            padding: '4px 12px',
            borderRadius: 16,
            border: '1px solid',
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: slipSort === 'desc' ? 700 : 500,
            background: slipSort === 'desc' ? '#eff6ff' : '#fff',
            color: '#444',
            borderColor: slipSort === 'desc' ? '#3b82f6' : '#e5e7eb',
          }}
        >
          ใหม่ก่อน (DESC)
        </button>
      </div>

      <div style={{ marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <p style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>ช่วงเวลาอัปโหลดสลิป</p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {([
            ['all', 'ทั้งหมด'],
            ['since_1', '1 วัน'],
            ['since_3', '3 วัน'],
            ['since_7', '7 วัน'],
            ['custom', 'กำหนดเอง'],
          ] as const).map(([v, label]) => (
            <button
              key={v}
              type="button"
              onClick={() => setTimePreset(v)}
              style={{
                padding: '4px 12px',
                borderRadius: 16,
                border: '1px solid',
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: timePreset === v ? 700 : 500,
                background: timePreset === v ? '#ecfdf5' : '#fff',
                color: '#444',
                borderColor: timePreset === v ? '#34d399' : '#e5e7eb',
              }}
            >
              {label}
            </button>
          ))}
        </div>
        {timePreset === 'custom' ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginTop: 10 }}>
            <label style={{ fontSize: 12, color: '#666' }}>
              ตั้งแต่
              <input
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                style={{ marginLeft: 8, padding: '6px 10px', borderRadius: 8, border: '1.5px solid #e5e7eb', fontSize: 13 }}
              />
            </label>
            <label style={{ fontSize: 12, color: '#666' }}>
              ถึง
              <input
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                style={{ marginLeft: 8, padding: '6px 10px', borderRadius: 8, border: '1.5px solid #e5e7eb', fontSize: 13 }}
              />
            </label>
            <span style={{ fontSize: 11, color: '#a8a29e' }}>วันที่ตามปฏิทินไทย (เที่ยงคืน ICT)</span>
          </div>
        ) : null}
      </div>

      <div style={{ marginBottom: 20, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
        <label style={{ flex: '1 1 220px', minWidth: 0, margin: 0 }}>
          <span style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>ค้นหา</span>
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="เลขออเดอร์, user ID, UUID สลิป, ชื่อ LINE, LIFF ID…"
            style={{ width: '100%', padding: '10px 14px', borderRadius: 12, border: '1.5px solid #e5e7eb', fontSize: 14, boxSizing: 'border-box' }}
            autoComplete="off"
          />
        </label>
        {debouncedSearch ? (
          <button
            type="button"
            onClick={() => { setSearchInput(''); setDebouncedSearch(''); }}
            style={{ padding: '10px 14px', borderRadius: 12, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            ล้างคำค้น
          </button>
        ) : null}
      </div>

      {error && <div style={{ padding: 14, borderRadius: 12, background: '#fff0f0', color: '#c00', marginBottom: 16 }}>{error}</div>}

      {!loading && total > 0 && showSlipCards && (
        <p style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
          แสดง {rangeStart}–{rangeEnd} จาก {total.toLocaleString()} รายการ (หน้า {page})
        </p>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#aaa' }}>กำลังโหลด...</div>
      ) : (
        <>
          {showExpiredNoOrders && (
            <p style={{ fontSize: 13, color: '#444', marginBottom: 16, padding: '12px 14px', background: '#fafaf9', borderRadius: 12, border: '1px solid #e7e5e4', lineHeight: 1.55 }}>
              ถ้าลูกค้า<strong>ส่งสลิปแล้ว</strong>และยังอยู่คิว &quot;รอตรวจ&quot; — ออเดอร์อาจยังเป็น<strong>รอดำเนินการ</strong>อยู่ได้ (เก็บคิวไว้ให้แอดมิน) และจะไม่ไปอยู่ในกลุ่ม &quot;ยังไม่อัปโหลดสลิป&quot; ด้านล่างนี้
            </p>
          )}
          {showExpiredNoSlip ? (
            <div style={{ marginBottom: pays.length > 0 ? 28 : 0 }}>
              <p style={{ fontSize: 12, fontWeight: 700, color: '#57534e', marginBottom: 12 }}>
                ออเดอร์หมดเวลา — ยังไม่อัปโหลดสลิป ({expiredNoSlip.length} รายการ)
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {expiredNoSlip.map((row) => (
                  <div
                    key={row.order_id}
                    style={{
                      textAlign: 'left',
                      background: '#fff',
                      borderRadius: 16,
                      padding: '16px 20px',
                      border: '1.5px solid #e7e5e4',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 16,
                      width: '100%',
                      boxSizing: 'border-box',
                    }}
                  >
                    <div
                      style={{
                        width: 56,
                        height: 56,
                        borderRadius: 10,
                        background: '#f5f5f4',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 11,
                        fontWeight: 600,
                        color: '#a8a29e',
                        textAlign: 'center',
                        padding: 4,
                        lineHeight: 1.2,
                      }}
                    >
                      ไม่มีสลิป
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: 13, fontWeight: 600, color: '#555', fontFamily: 'monospace', wordBreak: 'break-all' }}>{row.order_id}</p>
                      {row.line_display_name?.trim() ? (
                        <p style={{ fontSize: 12, color: '#737373', marginTop: 2 }}>👤 {row.line_display_name.trim()}</p>
                      ) : null}
                      <p style={{ fontSize: 11, color: '#a8a29e', marginTop: 2, fontFamily: 'monospace' }}>User: {row.user_id}</p>
                      <p style={{ fontSize: 12, color: '#aaa', marginTop: 2 }}>{fmt(row.created_at)}</p>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end', flexShrink: 0 }}>
                      {badge(orderStatusLabel.expired, 'expired', orderStatusColor)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {pays.length > 0 ? (
            <>
              {showExpiredNoSlip ? (
                <p style={{ fontSize: 12, fontWeight: 700, color: '#444', marginBottom: 12 }}>
                  รายการสลิป ({total.toLocaleString()} ในการแบ่งหน้า)
                </p>
              ) : null}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {pays.map(p => (
                <button key={p.id} type="button" onClick={() => onSelect(p)}
                  style={{ textAlign: 'left', background: '#fff', borderRadius: 16, padding: '16px 20px', cursor: 'pointer', border: '1.5px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 16, transition: 'box-shadow 0.15s', width: '100%' }}
                  onMouseEnter={e => (e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.10)')}
                  onMouseLeave={e => (e.currentTarget.style.boxShadow = 'none')}>
                  <div style={{ width: 56, height: 56, borderRadius: 10, overflow: 'hidden', background: '#f3f4f6', flexShrink: 0 }}>
                    {p.id && <img src={`${BASE_URL}/slip-image/${p.id}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 13, fontWeight: 600, color: '#555', fontFamily: 'monospace', wordBreak: 'break-all' }}>{p.order_id}</p>
                    {p.line_display_name?.trim() && (
                      <p style={{ fontSize: 12, color: '#737373', marginTop: 2 }}>👤 {p.line_display_name.trim()}</p>
                    )}
                    <p style={{ fontSize: 12, color: '#aaa', marginTop: 2 }}>{fmt(p.created_at)}</p>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end', flexShrink: 0 }}>
                    {badge(slipPaymentStatusLabel[p.status] ?? p.status, p.status, slipPaymentStatusColor)}
                    {p.order_status
                      ? badge(orderStatusLabel[p.order_status] ?? p.order_status, p.order_status, orderStatusColor)
                      : null}
                  </div>
                  <span style={{ color: '#ccc', fontSize: 18 }} aria-hidden>›</span>
                </button>
              ))}
            </div>
            </>
          ) : entirelyEmpty ? (
            <div style={{ textAlign: 'center', padding: 60 }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
              <p style={{ color: '#888' }}>ไม่มีรายการ</p>
            </div>
          ) : null}
        </>
      )}

      {!loading && pays.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 24, flexWrap: 'wrap', gap: 12 }}>
          <span style={{ fontSize: 13, color: '#888' }}>{total.toLocaleString()} รายการทั้งหมด</span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              type="button"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              style={{ padding: '8px 16px', borderRadius: 20, border: '1.5px solid #e5e7eb', background: page <= 1 ? '#f9fafb' : '#fff', cursor: page <= 1 ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, color: '#444' }}>
              ← ก่อนหน้า
            </button>
            <span style={{ fontSize: 13, fontWeight: 600, minWidth: 72, textAlign: 'center' }}>หน้า {page}</span>
            <button
              type="button"
              disabled={!hasMore || loading}
              onClick={() => setPage((p) => p + 1)}
              style={{ padding: '8px 16px', borderRadius: 20, border: '1.5px solid #e5e7eb', background: !hasMore ? '#f9fafb' : '#fff', cursor: !hasMore ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, color: '#444' }}>
              ถัดไป →
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SlipListPage;
